import type {
  SalesOrderBulkResult,
  SalesOrderFormValues,
  SalesOrderStatusHistoryItem,
  SalesOrderView,
} from "@cx/contracts";
import { ACTION_LABELS, type EffectState, type FreightMode } from "@cx/domain";

// ===== Pedidos de venda: estado do formulário e conversões (RF-VEN-02) =====

/** Linha do formulário. `listPriceCents` nulo em produto = preço da lista (o servidor resolve e congela ao salvar). */
export type ItemRow = {
  key: string;
  productId: string | null;
  description: string;
  code: string | null;
  unit: string;
  qtyQ4: number | null;
  listPriceCents: number | null;
  discountPct4: number | null;
  /** Depósito próprio do item (gravado); a tela não muda, só preserva. */
  warehouseId: string | null;
};

export type SalesOrderForm = {
  date: string;
  expectedDate: string;
  contact: { id: string; name: string } | null;
  customerPo: string;
  sellerId: string;
  priceListId: string;
  warehouseId: string;
  items: ItemRow[];
  discountMode: "value" | "pct";
  discountCents: number | null;
  discountPct4: number | null;
  freightCents: number | null;
  otherExpensesCents: number | null;
  paymentTermId: string;
  carrierId: string;
  freightMode: FreightMode;
  notes: string;
  internalNotes: string;
};

let seq = 0;
export const rowKey = () => `o${++seq}`;

export function emptyOrderForm(today: string, sellerId = ""): SalesOrderForm {
  return {
    date: today,
    expectedDate: "",
    contact: null,
    customerPo: "",
    sellerId,
    priceListId: "",
    warehouseId: "",
    items: [],
    discountMode: "value",
    discountCents: null,
    discountPct4: null,
    freightCents: null,
    otherExpensesCents: null,
    paymentTermId: "",
    carrierId: "",
    freightMode: 9,
    notes: "",
    internalNotes: "",
  };
}

/** Pedido gravado → formulário de edição. Os preços vêm congelados (explícitos). */
export function formFromView(o: SalesOrderView): SalesOrderForm {
  return {
    date: o.date,
    expectedDate: o.expectedDate ?? "",
    contact: { id: o.contact.id, name: o.contact.name },
    customerPo: o.customerPo ?? "",
    sellerId: o.seller?.id ?? "",
    priceListId: o.priceList?.id ?? "",
    warehouseId: o.warehouse?.id ?? "",
    items: o.items.map((i) => ({
      key: rowKey(),
      productId: i.productId,
      description: i.description,
      code: i.code,
      unit: i.unit ?? "",
      qtyQ4: i.qtyQ4,
      listPriceCents: i.listPriceCents,
      discountPct4: i.discountPct4,
      warehouseId: i.warehouseId === (o.warehouse?.id ?? null) ? null : i.warehouseId,
    })),
    discountMode: o.discountPct4 !== null ? "pct" : "value",
    discountCents: o.discountPct4 !== null ? null : o.discountCents,
    discountPct4: o.discountPct4,
    freightCents: o.freightCents,
    otherExpensesCents: o.otherExpensesCents,
    paymentTermId: o.paymentTerm?.id ?? "",
    carrierId: o.carrier?.id ?? "",
    freightMode: o.freightMode,
    notes: o.notes ?? "",
    internalNotes: o.internalNotes ?? "",
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

/** Formulário → corpo de POST/PUT /sales-orders (o servidor valida tudo de novo). */
export function orderBody(f: SalesOrderForm): SalesOrderFormValues {
  return {
    date: f.date,
    expectedDate: orNull(f.expectedDate),
    contactId: f.contact?.id ?? "",
    customerPo: orNull(f.customerPo),
    sellerId: orNull(f.sellerId),
    priceListId: orNull(f.priceListId),
    warehouseId: orNull(f.warehouseId),
    items: f.items.map((i) => ({
      productId: i.productId,
      description: i.productId ? null : orNull(i.description),
      unit: i.productId ? null : orNull(i.unit),
      qtyQ4: i.qtyQ4 ?? 0,
      listPriceCents: i.listPriceCents,
      discountPct4: i.discountPct4 ?? 0,
      warehouseId: i.warehouseId,
    })),
    discountPct4: f.discountMode === "pct" ? (f.discountPct4 ?? 0) : null,
    discountCents: f.discountMode === "value" ? (f.discountCents ?? 0) : 0,
    freightCents: f.freightCents ?? 0,
    otherExpensesCents: f.otherExpensesCents ?? 0,
    paymentTermId: orNull(f.paymentTermId),
    carrierId: orNull(f.carrierId),
    freightMode: f.freightMode,
    notes: orNull(f.notes),
    internalNotes: orNull(f.internalNotes),
  };
}

/** Corpo da prévia: null enquanto falta cliente ou item, ou há linha incompleta. */
export function previewBody(f: SalesOrderForm): SalesOrderFormValues | null {
  if (!f.contact || f.items.length === 0) return null;
  if (f.items.some((i) => !i.qtyQ4 || (!i.productId && (!i.description.trim() || i.listPriceCents === null))))
    return null;
  return { ...orderBody(f), notes: null, internalNotes: null, customerPo: null, expectedDate: null };
}

/** Erros de campo da API → linha do item (`items.0.qtyQ4` fica na linha 0). */
export function itemError(errors: Record<string, string>, index: number): string | undefined {
  const prefix = `items.${index}.`;
  const k = Object.keys(errors).find((e) => e.startsWith(prefix));
  return k ? errors[k] : undefined;
}

export const STOCK_EFFECT_LABELS: Readonly<Record<EffectState["stock"], string>> = {
  none: "Não lançado",
  reserved: "Reservado",
  posted: "Lançado",
};
export const FINANCIAL_EFFECT_LABELS: Readonly<Record<EffectState["financial"], string>> = {
  none: "Não lançadas",
  posted: "Lançadas",
};

/** Linha do histórico de situações: "Em aberto → Em andamento" ou a ação manual ("Lançar estoque"). */
export function historyLabel(
  h: Pick<SalesOrderStatusHistoryItem, "from" | "to" | "manual" | "steps">,
): string {
  if (h.manual) return h.steps.map((s) => ACTION_LABELS[s]).join(", ") || "Ação manual";
  return h.from ? `${h.from.label} → ${h.to.label}` : h.to.label;
}

export function historySteps(h: Pick<SalesOrderStatusHistoryItem, "manual" | "steps">): string {
  if (h.manual || h.steps.length === 0) return "";
  return h.steps.map((s) => ACTION_LABELS[s]).join(", ");
}

/** Resumo do lote para o toast: "2 pedidos alterados; 1 com erro." */
export function bulkSummary(r: Pick<SalesOrderBulkResult, "okCount" | "failCount">): string {
  const ok = r.okCount === 1 ? "1 pedido alterado" : `${r.okCount} pedidos alterados`;
  if (r.failCount === 0) return `${ok}.`;
  return `${ok}; ${r.failCount} com erro.`;
}
